import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output } from "@angular/core";
import { CartService } from "../../../cart.service";
import { ModalService } from "../../../../core/core";
import { Cart, CartItem, DeliveryOptions } from "../../../models/cart.model";
import { getMonthlyMultiplier, getWeeklyMultiplier, isWeeklyDuration } from "../../../rental-duration-billing";
import { CartItemRemoveConfirmationComponent } from "../../cart-item-remove-confirmation/cart-item-remove-confirmation.component";
import { SelfHaulInfoModalComponent } from "../../self-haul-info-modal/self-haul-info-modal.component";
import { Subscription } from "rxjs";

@Component({
  selector: "common-add-to-cart-details",
  templateUrl: "./add-to-cart-details.component.html",
  styleUrls: ["./add-to-cart-details.component.scss"],
})
export class AddToCartDetailsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() cartItem: CartItem;
  @Input() inCheckout: boolean;
  @Input() onMachineDetails: boolean;
  @Input() isEstimate: boolean;
  @Input() isQuote: boolean;
  @Input() comingFromDateDurationStep: boolean;
  @Input() deliveryOption: string; // Optional - will use cart's deliveryOption if not provided

  DeliveryOptions = DeliveryOptions;
  private cartSubscription: Subscription;

  @Output() edit = new EventEmitter<CartItem>();
  @Output() addToCart = new EventEmitter<null>();
  @Output() goToPreviousStep = new EventEmitter<null>();

  transportationEstimateRange: string;
  transportationEstimateExplanation: string;
  transportationTooltipText: string;

  rateLabel: string;
  rateSummary: any;
  attachmentRateProperty: string;
  total: number;
  rentalTotal: number;
  grandTotal: number;
  discountAmount: number = 0;
  discountPercent: number = 0;
  rentBeforeDiscount: number = 0;

  attachmentsUnavailable: string[];
  isInventoryGroupMachine: boolean;
  toExpensiveToEstimate: boolean;

  constructor(private modalService: ModalService, private cartService: CartService) {}

  ngOnInit() {
    // Subscribe to cart changes to update when deliveryOption changes
    this.cartSubscription = this.cartService.cartBSubject.subscribe((cart: Cart) => {
      if (cart?.deliveryOption && !this.deliveryOption) {
        // Only recalculate if we're using cart's deliveryOption (not input)
        this.recalculate();
      }
    });
  }

  ngOnDestroy() {
    if (this.cartSubscription) {
      this.cartSubscription.unsubscribe();
    }
  }

  // Get effective delivery option - use input if provided, otherwise get from cart
  getEffectiveDeliveryOption(): string {
    if (this.deliveryOption) {
      return this.deliveryOption;
    }
    return this.cartService.cart?.deliveryOption || DeliveryOptions.Delivery;
  }

  ngOnChanges() {
    this.recalculate();
  }

  recalculate() {
    if (!this.cartItem) return;

    const duration = this.cartItem.duration ?? 0;
    // Rentals shorter than 21 days are billed weekly; 21+ days are billed monthly.
    const isWeekly = isWeeklyDuration(duration);

    if (isWeekly) {
      this.rateSummary = this.cartItem.weeklySummary;
      this.rateLabel = "Weekly";
      this.attachmentRateProperty = "weeklyPrice";
    } else {
      this.rateSummary = this.cartItem.monthlySummary;
      this.rateLabel = "Monthly";
      this.attachmentRateProperty = "price";
    }

    // Read discount info from backend charges (applied server-side)
    this.discountAmount = 0;
    this.discountPercent = 0;
    this.rentBeforeDiscount = 0;

    if (this.cartItem.charges) {
      const rentCharge = this.cartItem.charges.find(c => c.chargeCategory === "Rent");
      if (rentCharge?.discountPercent > 0 && this.rateSummary?.Rent) {
        this.discountPercent = rentCharge.discountPercent;
        const discountMultiplier = (100 - this.discountPercent) / 100;
        this.rentBeforeDiscount = Math.round(this.rateSummary.Rent / discountMultiplier);
        this.discountAmount = this.rentBeforeDiscount - Math.round(this.rateSummary.Rent);
      }
    }

    let total = 0;
    for (const [key, value] of Object.entries(this.rateSummary)) {
      total += Math.round(value as number);
    }

    this.total = total;

    // Scale the single-period total (weekly or monthly) up to the whole rental:
    // full cycles + prorated remainder (3+ days → full week, 21+ days → full month).
    // Mirrors cart-cost-estimate + RentalDurationBilling on the backend.
    const periodMultiplier = isWeekly ? getWeeklyMultiplier(duration) : getMonthlyMultiplier(duration);
    this.rentalTotal = this.total * periodMultiplier;

    this.attachmentsUnavailable = this.cartItem.attachments.filter(d => d.status != "Available").map(d => d.title);
    this.toExpensiveToEstimate = this.cartItem.transportationEstimateSummary?.estimatedCost > 2500;

    // Calculate grand total including transportation (0 if self-transport/PickUp)
    const effectiveDeliveryOption = this.getEffectiveDeliveryOption();
    const transportationCost = (
      !this.toExpensiveToEstimate && 
      this.cartItem.transportationEstimateSummary?.estimatedCost &&
      effectiveDeliveryOption !== DeliveryOptions.PickUp
    ) ? this.cartItem.transportationEstimateSummary.estimatedCost : 0;
    this.grandTotal = this.rentalTotal + transportationCost;

    this.isInventoryGroupMachine = this.cartItem?.inventoryItem?.serialNumber?.startsWith("GROUP");
    const split = this.cartItem.transportationEstimateSummary?.internalNotes?.split(";");
    if (split) {
      this.transportationEstimateRange = split[0];
      this.transportationEstimateExplanation = split[1];
    }

    // Set transportation tooltip text
    if (this.cartItem.transportationEstimateSummary?.estimatedCost && !this.toExpensiveToEstimate) {
      this.transportationTooltipText = `Estimated round-trip delivery cost powered by Rubbl AI. ${this.transportationEstimateExplanation || ''} Final cost confirmed after order placement.`;
    } else if (this.toExpensiveToEstimate) {
      this.transportationTooltipText = "Due to distance, we need to take a closer look. Continue to place the order and we will provide the lowest quote within 1 business day.";
    } else {
      this.transportationTooltipText = "Enter your jobsite zip code during checkout to receive a transportation estimate.";
    }
  }

  confirmRemoveItem() {
    const modalRef = this.modalService.standardModal(CartItemRemoveConfirmationComponent, false);
    modalRef.componentInstance.cartItemTitle = this.cartItem.title;

    modalRef.result.then(
      (remove: boolean) => {
        if (remove) {
          this.cartService.removeItemFromCart(this.cartItem.id);
        }
      },
      () => {},
    );
  }

  removeAttachmentFromCartItem() {
    this.cartItem.attachments = this.cartItem.attachments.filter(d => d.status == "Available");
    this.cartService.updateCartItem(this.cartItem).subscribe(() => {});
  }

  removeFromCart() {
    this.cartService.removeItemFromCart(this.cartItem.id);
  }

  editClick() {
    this.edit.emit(this.cartItem);
  }

  addToCartClick() {
    this.addToCart.emit();
  }

  openSelfHaulRequirements() {
    this.modalService.standardModal(SelfHaulInfoModalComponent, false);
  }
}
